import { Badge } from '@src/design-system/badge';
import { useMemo } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';
import { LabelsSelect } from './labels-select';

import type { TLabel } from '@shared/types/label';
import { LabelBadge } from './label-badge';
import { cn } from '@src/design-system/helpers';
import LabelsTrigger from './labels-trigger';

interface ListLabelProps {
  data?: TLabel[];
  handleRemove?: (item: TLabel) => void;
  onChange?: (item: TLabel) => TLabel[];
  editable?: boolean;
  className?: string;
  handleUpdateLabelsSave?: (items?: TLabel[]) => void;
  withAddButton?: boolean;
  visibleCount?: number;
  badgeClassName?: string;
  removeIconClassName?: string;
  moreBadgeClassName?: string;
  popoverClassName?: string;
}

const ListLabelsPopoverContent = ({
  handleLabelChange,
  data,
  popoverClassName,
}: {
  handleLabelChange?: (item: TLabel) => TLabel[];
  data: TLabel[];
  popoverClassName?: string;
}) => {
  if (!handleLabelChange) return null;

  return (
    <PopoverContent
      withPortal={true}
      side='bottom'
      align='center'
      className={cn('size-auto shadow-lg p-0 rounded-lg', popoverClassName)}
    >
      <LabelsSelect
        onChange={handleLabelChange}
        labels={data}
      />
    </PopoverContent>
  );
};

export const ListLabel: React.FC<ListLabelProps> = ({
  data = [],
  editable = true,
  className = '',
  onChange: handleLabelChange,
  handleUpdateLabelsSave,
  withAddButton = true,
  visibleCount = 2,
  badgeClassName,
  removeIconClassName,
  popoverClassName,
}) => {
  const firstVisibleLabels = useMemo(
    () => data.slice(0, visibleCount),
    [data, visibleCount]
  );

  const remaining = useMemo(
    () => data.slice(visibleCount),
    [data, visibleCount]
  );

  const handleRemove = (label: TLabel) => {
    const updated = handleLabelChange?.(label);
    handleUpdateLabelsSave?.(updated);
  };

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-y-2 gap-x-1 max-w-[400px] relative',
        className
      )}
    >
      {firstVisibleLabels.map(({ id, hexa_color, name }) => {
        return (
          <LabelBadge
            key={id}
            label={{ id, hexa_color, name }}
            editable={editable}
            onRemove={() => handleRemove({ id, hexa_color, name })}
            badgeClassName={badgeClassName}
            removeIconClassName={removeIconClassName}
          />
        );
      })}
      {data.length > visibleCount && (
        <Popover onOpenChange={(open) => !open && handleUpdateLabelsSave?.()}>
          <PopoverTrigger asChild>
            <div
              onClick={(e) => e.stopPropagation()}
              className='cursor-pointer'
            >
              <Badge
                variant='outline'
                className={cn(
                  `flex items-center justify-center bg-primary-100 h-6 w-8 text-primary-1000! border-none! text-sm font-normal rounded-md`
                )}
              >
                +{remaining.length}
              </Badge>
            </div>
          </PopoverTrigger>
          <PopoverContent
            align='center'
            side='bottom'
            className={cn(
              'flex flex-wrap gap-x-2 gap-y-3 items-start max-h-[250px] scrollbar-gray overflow-auto max-w-[400px]',
              popoverClassName
            )}
          >
            {remaining.map(({ id, hexa_color, name }) => (
              <LabelBadge
                key={id}
                label={{ id, hexa_color, name }}
                editable={editable}
                onRemove={() => handleRemove({ id, hexa_color, name })}
              />
            ))}
            {editable && (
              <Popover>
                <LabelsTrigger />
                <ListLabelsPopoverContent
                  handleLabelChange={handleLabelChange}
                  data={data}
                  popoverClassName={popoverClassName}
                />
              </Popover>
            )}
          </PopoverContent>
        </Popover>
      )}
      {withAddButton && (
        <Popover onOpenChange={(open) => !open && handleUpdateLabelsSave?.()}>
          <LabelsTrigger />

          <ListLabelsPopoverContent
            handleLabelChange={handleLabelChange}
            data={data}
            popoverClassName={popoverClassName}
          />
        </Popover>
      )}
    </div>
  );
};
